import { useAtomValue } from 'jotai';
import { apiAccountsDetailsAtom, appAccountsDetailsAtom, videoAccountsDetailsAtom } from '../atoms';
import {
  useFetchAPIUsageMetric,
  useFetchAppUsageMetric,
  useFetchVideoUsageMetric,
  useFetchUsageMetricTable,
} from '@components/usage-dashboard/hooks/dataFetcherhooks';
import {
  useMakeExportData,
  useMakeTableExportData,
} from '@components/usage-dashboard/hooks/useMetricSeries';
import { EMPTY_LENGTH } from '@components/usage-dashboard/constant';

export const useExportMetricData = (product: string) => {
  const configMap = {
    EI: {
      accountsDetailsAtom: videoAccountsDetailsAtom,
      fetchUsageMetric: useFetchVideoUsageMetric,
      sectionKey: 'ud-video-metric-timeseries',
      accountType: 'Video',
    },
    APP: {
      accountsDetailsAtom: appAccountsDetailsAtom,
      fetchUsageMetric: useFetchAppUsageMetric,
      sectionKey: 'ud-app-metric-timeseries',
      accountType: 'App',
    },
    API: {
      accountsDetailsAtom: apiAccountsDetailsAtom,
      fetchUsageMetric: useFetchAPIUsageMetric,
      sectionKey: 'ud-api-metric-timeseries',
      accountType: 'API',
    },
  };

  const { accountsDetailsAtom, fetchUsageMetric, sectionKey, accountType } = configMap[product];

  const accountDetails = useAtomValue(accountsDetailsAtom);
  const hasApiAccount = accountDetails.length > EMPTY_LENGTH;

  const { data: seriesData } = fetchUsageMetric({
    enabled: hasApiAccount,
    product,
  });

  useMakeExportData({
    seriesData: seriesData,
    sectionKey: sectionKey,
    title: `${accountType} Usage Metric`,
  });
};

// Registers the per-account table breakdown for export. Mounted independently of
// the on-screen view so the table is always part of the download.
export const useTableExportMetricData = (product: string) => {
  const configMap = {
    EI: {
      accountsDetailsAtom: videoAccountsDetailsAtom,
      sectionKey: 'ud-video-metric-table',
      accountType: 'Video',
    },
    APP: {
      accountsDetailsAtom: appAccountsDetailsAtom,
      sectionKey: 'ud-app-metric-table',
      accountType: 'App',
    },
    API: {
      accountsDetailsAtom: apiAccountsDetailsAtom,
      sectionKey: 'ud-api-metric-table',
      accountType: 'API',
    },
  };

  const { accountsDetailsAtom, sectionKey, accountType } = configMap[product];

  const accountDetails = useAtomValue(accountsDetailsAtom);
  const hasAccount = accountDetails.length > EMPTY_LENGTH;

  const { data: tableData } = useFetchUsageMetricTable({
    enabled: hasAccount,
    product,
  });

  useMakeTableExportData({
    tableData,
    accountDetails,
    sectionKey,
    title: `${accountType} Usage Metric - By Account`,
  });
};
